import { useLoad } from '@hive/ui';
import { Link } from 'react-router';
import { useI18nP } from '../../i18n';
import { useSession } from '../../lib/session';
import { rcaApi } from '../../lib/slarca';
import { useCauseLabel } from './causeLabel';

type Summary = Awaited<ReturnType<typeof rcaApi.summary>>;

/**
 * Cartão compacto do Início: as principais causas dos últimos 30 dias, "X% em N causas", % evitável e o que falta
 * classificar. O Início passa o resumo já carregado (`summary`) para o cartão entrar junto com o painel lateral, sem
 * empurrar os cartões de baixo depois do primeiro desenho; sem ele, o cartão lê sozinho.
 */
export function RootCausesCard({ summary }: { summary?: Summary | null }) {
  const { t, fmt } = useI18nP();
  const s = useSession();
  const label = useCauseLabel();
  const given = summary !== undefined;
  const v = useLoad(() => (given ? Promise.resolve(null) : rcaApi.summary(s, 30).catch(() => null)), [s.api, given]);
  const d = given ? summary : v.data;
  if (!d) return null;
  const max = Math.max(1, ...d.top.map((x) => x.count));
  return (
    <section className="mini-panel" aria-labelledby="home-causes">
      <h2 id="home-causes">{t('rca.home.title')}</h2>
      {d.total === 0 ? (
        <div className="foot-note">{t('rca.home.none')}</div>
      ) : (
        <>
          {d.headline.causes > 0 && <b className="small">{t('rca.headline', { count: d.headline.causes, pct: fmt.number(d.headline.pct) })}</b>}
          <ul className="stack-sm" style={{ listStyle: 'none', margin: '6px 0', padding: 0 }}>
            {d.top.map((x) => (
              <li key={x.key} className="small">
                <div className="row between">
                  <span>{label(x.key)}</span>
                  <span className="mono">{fmt.number(x.count)}</span>
                </div>
                <span aria-hidden="true" style={{ display: 'block', height: 4, borderRadius: 2, background: x.preventable ? 'var(--warn)' : 'var(--accent)', width: `${(100 * x.count) / max}%` }} />
              </li>
            ))}
          </ul>
          <div className="foot-note">
            {t('rca.home.foot', { pct: fmt.number(d.preventablePct), count: d.unclassified })} · <Link to="/root-causes">{t('rca.home.open')}</Link>
          </div>
        </>
      )}
    </section>
  );
}
